import React, { useState } from 'react';
import apiClient from '../services/api';
import { UserProfile, UpgradePromptData, SubscriptionTier } from '../types/api';
import UpgradePrompt from './UpgradePrompt';
import './ImageGeneration.css';

interface ImageGenerationProps {
  chapterId: string;
  chapterTitle: string;
  userProfile: UserProfile;
  onImageGenerated?: (imageUrl: string) => void;
  onUsageUpdate?: () => void;
}

export const ImageGeneration: React.FC<ImageGenerationProps> = ({
  chapterId,
  chapterTitle,
  userProfile,
  onImageGenerated,
  onUsageUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imagePrompt, setImagePrompt] = useState<string>('');
  const [generatedImage, setGeneratedImage] = useState<{
    url: string;
    prompt: string;
    size: number;
  } | null>(null);
  const [imageCredits, setImageCredits] = useState<number>(50); // Default to 50, will be updated from API
  const [step, setStep] = useState<'generate-prompt' | 'review-prompt' | 'generating-image' | 'complete'>('generate-prompt');
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);

  // Check if user has reached their AI usage limit
  const hasReachedLimit = userProfile.ai_usage_count >= userProfile.ai_usage_limit;

  // Upgrade popup data
  const upgradeData: UpgradePromptData = {
    type: 'ai_usage_limit',
    current_plan: userProfile.subscription_tier,
    suggested_plan: userProfile.subscription_tier?.toLowerCase() === 'premium' ? 'ai_credits' as SubscriptionTier : 'premium' as SubscriptionTier,
    message: 'Purchase more AI credits to continue generating images.',
    limits: {
      current: userProfile.ai_usage_count,
      limit: userProfile.ai_usage_limit,
    },
  };

  const handleGeneratePrompt = async () => {
    // Check if user has reached limit before making request
    if (hasReachedLimit) {
      setShowUpgradePopup(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.generateImagePrompt(chapterId);
      setImagePrompt(response.image_prompt);
      setStep('review-prompt');

      // Update usage count
      if (onUsageUpdate) {
        onUsageUpdate();
      }
    } catch (err: any) {
      console.error('Failed to generate image prompt:', err);
      // Check for 429 (rate limit / usage limit exceeded)
      if (err?.response?.status === 429 || err?.status === 429) {
        setShowUpgradePopup(true);
      } else {
        setError(err?.detail || 'Failed to generate image prompt. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    // Check if user has reached limit before making request
    if (hasReachedLimit) {
      setShowUpgradePopup(true);
      return;
    }

    setLoading(true);
    setError(null);
    setStep('generating-image');

    try {
      const response = await apiClient.generateImage(imagePrompt, chapterId);
      setGeneratedImage({
        url: response.image_url,
        prompt: response.prompt_used,
        size: response.size,
      });

      // Update credits from response if available
      if (response.usage_info?.credits_used) {
        setImageCredits(response.usage_info.credits_used);
      }

      setStep('complete');

      // Update usage count
      if (onUsageUpdate) {
        onUsageUpdate();
      }

      // Notify parent component - image is automatically inserted
      if (onImageGenerated) {
        onImageGenerated(response.image_url);
      }
    } catch (err: any) {
      console.error('Failed to generate image:', err);
      // Check for 429 (rate limit / usage limit exceeded)
      if (err?.response?.status === 429 || err?.status === 429) {
        setShowUpgradePopup(true);
        setStep('review-prompt');
      } else {
        setError(err?.detail || 'Failed to generate image. Please try again.');
        setStep('review-prompt');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep('generate-prompt');
    setImagePrompt('');
    setGeneratedImage(null);
    setError(null);
  };

  return (
    <>
      {/* Upgrade popup - shown when user tries to use image generation with no credits */}
      {showUpgradePopup && (
        <UpgradePrompt
          data={upgradeData}
          onClose={() => setShowUpgradePopup(false)}
          onUpgrade={() => {
            window.location.href = '/subscription';
          }}
        />
      )}
    <div className="image-generation-container">
      <div className="image-generation-header">
        <h3>AI Image Generation</h3>
        <p className="image-generation-subtitle">
          Generate an image based on your chapter: <strong>{chapterTitle}</strong>
        </p>
      </div>

      {error && (
        <div className="image-generation-error">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
        </div>
      )}

      {/* Step 1: Generate Prompt */}
      {step === 'generate-prompt' && (
        <div className="image-generation-step">
          <p className="step-description">
            Click below to analyze your chapter and generate an image prompt. We'll create a detailed description based on your chapter's content, themes, and atmosphere.
          </p>
          <button
            className="btn-primary btn-generate"
            onClick={handleGeneratePrompt}
            disabled={loading}
          >
            {loading ? 'Analyzing Chapter...' : 'Generate Image Prompt'}
            {!loading && <small className="credit-cost-inline">(1 AI credit)</small>}
          </button>
        </div>
      )}

      {/* Step 2: Review & Edit Prompt */}
      {step === 'review-prompt' && (
        <div className="image-generation-step">
          <div className="prompt-review-section">
            <label htmlFor="image-prompt">Image Prompt:</label>
            <textarea
              id="image-prompt"
              className="prompt-textarea"
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              rows={6}
              placeholder="Edit the generated prompt if needed..."
            />
            <p className="prompt-hint">
              💡 You can edit this prompt to refine the image generation
            </p>
          </div>

          <div className="button-group">
            <button
              className="btn-secondary"
              onClick={handleReset}
              disabled={loading}
            >
              ← Start Over
            </button>
            <button
              className="btn-primary btn-generate"
              onClick={handleGenerateImage}
              disabled={loading || !imagePrompt.trim()}
            >
              {loading ? 'Generating Image...' : 'Generate Image'}
              {!loading && <small className="credit-cost-inline">(50 AI credits)</small>}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Generating Image */}
      {step === 'generating-image' && (
        <div className="image-generation-step">
          <div className="prompt-review-section">
            <label htmlFor="image-prompt-generating">Image Prompt:</label>
            <textarea
              id="image-prompt-generating"
              className="prompt-textarea"
              value={imagePrompt}
              readOnly
              rows={6}
            />
          </div>
          <div className="generating-spinner">
            <div className="spinner"></div>
            <p>Creating your image...</p>
            <p className="generating-note">This may take up to 60 seconds</p>
          </div>
        </div>
      )}

      {/* Step 4: Image Generated */}
      {step === 'complete' && generatedImage && (
        <div className="image-generation-step">
          <div className="success-message">
            <p>✅ Image generated and inserted into your chapter</p>
          </div>

          <div className="prompt-review-section">
            <label htmlFor="image-prompt-complete">Image Prompt:</label>
            <textarea
              id="image-prompt-complete"
              className="prompt-textarea"
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              rows={6}
            />
          </div>

          <div className="button-group">
            <button
              className="btn-secondary"
              onClick={handleReset}
            >
              ← Start Over
            </button>
            <button
              className="btn-primary btn-generate"
              onClick={handleGenerateImage}
              disabled={loading || !imagePrompt.trim()}
            >
              Regenerate Image
              <small className="credit-cost-inline">(50 AI credits)</small>
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ImageGeneration;
